import MapBrowserEvent from "ol/MapBrowserEvent";

import Feature, { FeatureLike } from "ol/Feature";
import { Geometry } from "ol/geom";

import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useEffect, useRef } from "react";
import { useMap } from "./useMap";

interface useInteractionEventArgs {
  annotation: VectorLayer<VectorSource<Feature<Geometry>>>;
  onClick?: (feature: Feature<Geometry>) => void;
  onHover?: (feature: Feature<Geometry>, pixel?: number[]) => void;
  onLeave?: () => void;
  isDisabledSelection?: boolean;
}

export function useInteractionEvent({
  annotation,
  onClick,
  onHover,
  onLeave,
  isDisabledSelection = false,
}: useInteractionEventArgs) {
  const map = useMap();

  // ref로 최신 콜백 유지 — 이벤트 핸들러가 항상 최신 값을 참조
  const callbacksRef = useRef({ annotation, onClick, onHover, onLeave });
  useEffect(() => {
    callbacksRef.current = { annotation, onClick, onHover, onLeave };
  }, [annotation, onClick, onHover, onLeave]);

  // 안정적인 이벤트 핸들러 등록 — map 변경 시에만 재등록
  useEffect(() => {
    const clickHandler = (e: MapBrowserEvent<any>) => {
      const { annotation: ann, onClick: click } = callbacksRef.current;
      const pixel = e.pixel;

      // 겹쳐있는 마커 위에서부터 선택되도록 리버스
      const reversedFeature: FeatureLike[] = map
        .getFeaturesAtPixel(pixel)
        .reverse();

      reversedFeature.forEach((feature) => {
        if (!feature.getProperties().shape) return;

        if (ann?.getSource()?.getFeatures()[0] === feature) {
          click && click(feature as Feature<Geometry>);
        }
      });
    };

    const hoverHandler = (e: MapBrowserEvent<any>) => {
      const {
        annotation: ann,
        onHover: hover,
        onLeave: leave,
      } = callbacksRef.current;
      if (!hover) return;
      const pixel = e.pixel;

      const reversedFeature: FeatureLike[] = map
        .getFeaturesAtPixel(pixel)
        .reverse();

      if (reversedFeature.length === 0) {
        leave && leave();
      }
      reversedFeature.forEach((feature) => {
        if (!feature.getProperties().shape) return;

        if (ann?.getSource()?.getFeatures()[0] === feature) {
          hover(feature as Feature<Geometry>, pixel);
        }
      });
    };

    map.on("click", clickHandler);
    map.on("pointermove", hoverHandler);
    return () => {
      map.un("click", clickHandler);
      map.un("pointermove", hoverHandler);
    };
  }, [map]);
}
